/** @jsxImportSource @emotion/react */
import React, { memo, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { DatePicker, Portal } from '@ark-ui/react';
import type { DatePickerValueChangeDetails, UseDatePickerContext } from '@ark-ui/react';
import { CalendarDate, CalendarDateTime, today, getLocalTimeZone } from '@internationalized/date';
import type { DateValue } from '@internationalized/date';
import dayjs from 'dayjs';
import { useTheme } from '../provider/useTheme';
import { useTranslation } from '@vitvio/locales';
import CoreIcon from './CoreIcon';
import { TimeSelect } from './TimeSelect';
import {
  triggerStyle,
  triggerIconStyle,
  triggerTextStyle,
  contentStyle,
  timeContainerStyle,
  timeInputStyle,
  errorStyle,
  type DatepickerStyleTokens,
} from './CoreDatepicker.styles';

/**
 * Props for the CoreDatepicker component
 */
export interface CoreDatepickerProps {
  /** Currently selected date/time value */
  value?: dayjs.Dayjs | null;
  /** Callback fired when the date/time value changes */
  onChange?: (date: dayjs.Dayjs | null) => void;
  /** Error message translation key to display below the input */
  error?: string;
  /** Placeholder text translation key */
  placeholder?: string;
  /** Picker type: date, datetime, or time */
  type?: 'date' | 'datetime' | 'time';
  /** Minimum selectable date (dayjs instance or 'today') */
  min?: dayjs.Dayjs | 'today';
  /** Maximum selectable date (dayjs instance or 'today') */
  max?: dayjs.Dayjs | 'today';
}

function resolveI18nText(candidate: string, fallback: string): string {
  const trimmed = candidate.trim();
  if (!trimmed) {
    return fallback;
  }
  const looksLikeKey = /^[a-z][\w-]*(?::[a-z][\w-]*)?(?:\.[\w-]+)+$/i.test(trimmed);
  return looksLikeKey ? fallback : trimmed;
}

function dayjsToCalendarDate(d: dayjs.Dayjs): CalendarDate {
  return new CalendarDate(d.year(), d.month() + 1, d.date());
}

function dayjsToCalendarDateTime(d: dayjs.Dayjs): CalendarDateTime {
  return new CalendarDateTime(d.year(), d.month() + 1, d.date(), d.hour(), d.minute());
}

function dateValueToDayjs(dv: DateValue): dayjs.Dayjs {
  if ('hour' in dv) {
    const dt = dv as CalendarDateTime;
    return dayjs(new Date(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute));
  }
  return dayjs(new Date(dv.year, dv.month - 1, dv.day));
}

function resolveMinMax(bound: dayjs.Dayjs | 'today' | undefined): DateValue | undefined {
  if (bound === 'today') return today(getLocalTimeZone());
  if (bound) return dayjsToCalendarDate(bound);
  return undefined;
}

/** Shared navigation row rendered inside each DatePicker.View. */
function CalendarViewControl() {
  return (
    <DatePicker.ViewControl>
      <DatePicker.PrevTrigger>
        <CoreIcon icon="chevron_back" size="md" color="fg" />
      </DatePicker.PrevTrigger>
      <DatePicker.ViewTrigger>
        <DatePicker.RangeText />
      </DatePicker.ViewTrigger>
      <DatePicker.NextTrigger>
        <CoreIcon icon="chevron_forward" size="md" color="fg" />
      </DatePicker.NextTrigger>
    </DatePicker.ViewControl>
  );
}

/**
 * Date/time picker component using Ark UI DatePicker.
 */
export const CoreDatepicker: React.FC<CoreDatepickerProps> = memo(
  ({ value, onChange, error = '', placeholder, type = 'date', min, max }) => {
    const theme = useTheme();
    const { getColor, getRadius, getFont, getSpacing } = theme;
    const { t } = useTranslation();
    const errorId = useId();

    const isTimeType = type === 'time';
    const isDatetime = type === 'datetime';
    const hasError = error !== '';

    // Internal time state for datetime mode — tracks hour:minute independently
    // so time persists when changing the date cell.
    const [internalTime, setInternalTime] = useState<{ hour: number; minute: number }>(() => {
      if (value && isDatetime) return { hour: value.hour(), minute: value.minute() };
      return { hour: 0, minute: 0 };
    });

    // Sync internalTime when external value changes (e.g. parent resets value)
    useEffect(() => {
      if (!isDatetime || !value) return;
      const h = value.hour();
      const m = value.minute();
      setInternalTime((prev) => (prev.hour === h && prev.minute === m ? prev : { hour: h, minute: m }));
    }, [value, isDatetime]);

    const formatDate = useCallback(
      (date: dayjs.Dayjs | null): string => {
        if (!date) return '';
        const formatKey = isDatetime ? 'common:format.datetime' : 'common:format.date';
        return date.format(t(formatKey));
      },
      [isDatetime, t],
    );

    const arkValue = useMemo<DateValue[] | undefined>(() => {
      if (!value) return undefined;
      if (isDatetime) return [dayjsToCalendarDateTime(value)];
      return [dayjsToCalendarDate(value)];
    }, [value, isDatetime]);

    const arkMin = useMemo(() => resolveMinMax(min), [min]);
    const arkMax = useMemo(() => resolveMinMax(max), [max]);

    const handleValueChange = useCallback(
      (details: DatePickerValueChangeDetails) => {
        const selected = details.value[0];
        if (!selected) {
          onChange?.(null);
          return;
        }
        if (isDatetime) {
          const dt = new CalendarDateTime(
            selected.year,
            selected.month,
            selected.day,
            internalTime.hour,
            internalTime.minute,
          );
          onChange?.(dateValueToDayjs(dt));
        } else {
          onChange?.(dateValueToDayjs(selected));
        }
      },
      [onChange, isDatetime, internalTime],
    );

    const handleTimeChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.currentTarget.value;
        if (!raw || !raw.includes(':')) return;
        const [h, m] = raw.split(':').map(Number);
        if (Number.isNaN(h) || Number.isNaN(m)) return;
        const hours = Math.min(Math.max(h, 0), 23);
        const minutes = Math.min(Math.max(m, 0), 59);
        setInternalTime({ hour: hours, minute: minutes });
        if (value) {
          const dt = new CalendarDateTime(value.year(), value.month() + 1, value.date(), hours, minutes);
          onChange?.(dateValueToDayjs(dt));
        }
      },
      [value, onChange],
    );

    const timeValue = useMemo(() => {
      if (!isDatetime) return '';
      return `${String(internalTime.hour).padStart(2, '0')}:${String(internalTime.minute).padStart(2, '0')}`;
    }, [isDatetime, internalTime]);

    const formattedValue = formatDate(value ?? null);
    const hasValue = formattedValue !== '';

    const tokens: DatepickerStyleTokens = useMemo(
      () => ({ getColor, getRadius, getFont, getSpacing, hasError, hasValue }),
      [getColor, getRadius, getFont, getSpacing, hasError, hasValue],
    );

    // Resolve BCP 47 locale tag; fallback if i18n returns the raw key or an invalid tag
    const localeTag = useMemo(() => {
      const raw = t('common:locale.bcp47');
      try {
        const [canonical] = Intl.getCanonicalLocales(raw);
        return canonical;
      } catch {
        return 'en-US';
      }
    }, [t]);

    const iconCss = useMemo(() => triggerIconStyle(getSpacing), [getSpacing]);

    // Error block
    const errorBlock = hasError && (
      <div id={errorId} role="alert" css={errorStyle(tokens)}>
        <CoreIcon icon="warning" size="md" color="fg.error" />
        <span>{t(error)}</span>
      </div>
    );

    // Time-only mode: delegate to the existing TimeSelect component
    if (isTimeType) {
      return (
        <div>
          <TimeSelect
            value={value ?? null}
            onChange={onChange}
            placeholderPath={placeholder}
            icon="schedule"
            isInvalid={hasError}
          />
          {errorBlock}
        </div>
      );
    }

    const timeInputLabel = resolveI18nText(t('common:time.selectTime'), 'Select time');
    const calendarDialogLabel = resolveI18nText(t('common:datepicker.calendar'), 'Calendar');
    const displayText = formattedValue || (placeholder ? t(placeholder) : '');

    return (
      <DatePicker.Root
        value={arkValue}
        onValueChange={handleValueChange}
        min={arkMin}
        max={arkMax}
        closeOnSelect={!isDatetime}
        lazyMount
        unmountOnExit
        positioning={{ placement: 'bottom-start', gutter: theme.sizes.layout.popoverGutter }}
        locale={localeTag}
        fixedWeeks
      >
        <DatePicker.Control>
          <DatePicker.Trigger
            data-testid="core-datepicker-trigger"
            css={triggerStyle(tokens)}
            aria-invalid={hasError || undefined}
            aria-describedby={hasError ? errorId : undefined}
          >
            <span css={iconCss}>
              <CoreIcon icon="calendar" size="md" color="fg" />
            </span>
            <span css={triggerTextStyle}>{displayText}</span>
            <span css={iconCss}>
              <CoreIcon icon="chevron_down" size="md" color="fg" />
            </span>
          </DatePicker.Trigger>
        </DatePicker.Control>

        {errorBlock}

        <Portal>
          <DatePicker.Positioner>
            <DatePicker.Content
              data-testid="core-datepicker-popup"
              aria-label={calendarDialogLabel}
              css={contentStyle(tokens)}
            >
              <DatePicker.View view="day">
                <DatePicker.Context>
                  {(datePicker: UseDatePickerContext) => (
                    <>
                      <CalendarViewControl />
                      <DatePicker.Table>
                        <DatePicker.TableHead>
                          <DatePicker.TableRow>
                            {datePicker.weekDays.map((weekDay: { short: string }, id: number) => (
                              <DatePicker.TableHeader key={id}>{weekDay.short}</DatePicker.TableHeader>
                            ))}
                          </DatePicker.TableRow>
                        </DatePicker.TableHead>
                        <DatePicker.TableBody>
                          {datePicker.weeks.map((week: DateValue[], id: number) => (
                            <DatePicker.TableRow key={id}>
                              {week.map((day: DateValue, id: number) => (
                                <DatePicker.TableCell key={id} value={day}>
                                  <DatePicker.TableCellTrigger>{day.day}</DatePicker.TableCellTrigger>
                                </DatePicker.TableCell>
                              ))}
                            </DatePicker.TableRow>
                          ))}
                        </DatePicker.TableBody>
                      </DatePicker.Table>
                    </>
                  )}
                </DatePicker.Context>
              </DatePicker.View>

              <DatePicker.View view="month">
                <DatePicker.Context>
                  {(datePicker: UseDatePickerContext) => (
                    <>
                      <CalendarViewControl />
                      <DatePicker.Table>
                        <DatePicker.TableBody>
                          {datePicker
                            .getMonthsGrid({ columns: 4, format: 'short' })
                            .map((months: { label: string; value: number }[], id: number) => (
                              <DatePicker.TableRow key={id}>
                                {months.map((month: { label: string; value: number }, id: number) => (
                                  <DatePicker.TableCell key={id} value={month.value}>
                                    <DatePicker.TableCellTrigger>{month.label}</DatePicker.TableCellTrigger>
                                  </DatePicker.TableCell>
                                ))}
                              </DatePicker.TableRow>
                            ))}
                        </DatePicker.TableBody>
                      </DatePicker.Table>
                    </>
                  )}
                </DatePicker.Context>
              </DatePicker.View>

              <DatePicker.View view="year">
                <DatePicker.Context>
                  {(datePicker: UseDatePickerContext) => (
                    <>
                      <CalendarViewControl />
                      <DatePicker.Table>
                        <DatePicker.TableBody>
                          {datePicker
                            .getYearsGrid({ columns: 4 })
                            .map((years: { label: string; value: number }[], id: number) => (
                              <DatePicker.TableRow key={id}>
                                {years.map((year: { label: string; value: number }, id: number) => (
                                  <DatePicker.TableCell key={id} value={year.value}>
                                    <DatePicker.TableCellTrigger>{year.label}</DatePicker.TableCellTrigger>
                                  </DatePicker.TableCell>
                                ))}
                              </DatePicker.TableRow>
                            ))}
                        </DatePicker.TableBody>
                      </DatePicker.Table>
                    </>
                  )}
                </DatePicker.Context>
              </DatePicker.View>

              {isDatetime && (
                <div
                  data-testid="core-datepicker-time-input"
                  role="group"
                  aria-label={timeInputLabel}
                  css={timeContainerStyle(tokens)}
                >
                  <CoreIcon icon="schedule" size="sm" color="fg.muted" />
                  <input
                    type="time"
                    value={timeValue}
                    onChange={handleTimeChange}
                    aria-label={timeInputLabel}
                    data-testid="core-datepicker-time-value"
                    css={timeInputStyle(tokens)}
                  />
                </div>
              )}
            </DatePicker.Content>
          </DatePicker.Positioner>
        </Portal>
      </DatePicker.Root>
    );
  },
);

CoreDatepicker.displayName = 'CoreDatepicker';

export default CoreDatepicker;
